<template>
    <div class="filterbar flex grow flex-wrap justify-between gap-4">
        <collapsable-filter-bar v-model="isOpenState" :active="isActive" :is-processing="processing" @clear="onClear">
            <template v-if="searchFilter || $slots['visible-filter']" #visible-filter="{ isOpen }">
                <component
                    :is="SearchFilter"
                    v-if="searchFilter"
                    :is-processing="processing"
                    :model-value="getValue(searchFilter.key) || ''"
                    :placeholder="searchFilter.placeholder || trans('general.search.placeholder')"
                    :show-spinner="!isOpen"
                    @update:modelValue="updateValue(searchFilter.key, $event)"
                />
            </template>

            <template v-for="filter in renderedFilters" :key="filter.key">
                <div
                    v-if="filter.type === 'multi-select' && filter.options === null"
                    :class="filter.class"
                    class="skeleton-loader__bone loading-ripple h-8 w-32 rounded-md"
                />
                <component
                    :is="MultiSelectDropdown"
                    v-else-if="filter.type === 'multi-select'"
                    :class="filter.class"
                    :data-testid="`filter-${filter.key}`"
                    :label="filter.label"
                    :model-value="getValue(filter.key) || []"
                    :name="filter.key"
                    :options="filter.options || []"
                    :show-searchbar="filter.showSearchbar ?? false"
                    :show-select-all-button="false"
                    :teleport-popover="teleportPopovers"
                    @update:modelValue="updateValue(filter.key, $event)"
                >
                    <template #label="{ option, show }">
                        <slot :name="`${filter.key}-option`" :option="option" :show="show">
                            <span class="truncate">{{ option.label }}</span>
                        </slot>
                    </template>
                </component>

                <component
                    :is="SingleSelectDropdown"
                    v-else-if="filter.type === 'single-select'"
                    :class="filter.class"
                    :data-testid="`filter-${filter.key}`"
                    :label="filter.label"
                    :model-value="getValue(filter.key) ?? null"
                    :name="filter.key"
                    :options="filter.options || []"
                    :teleport-popover="teleportPopovers"
                    @update:modelValue="updateValue(filter.key, $event)"
                >
                    <template #label="{ option }">
                        <slot :name="`${filter.key}-option`" :option="option">
                            <span class="truncate">{{ option.label }}</span>
                        </slot>
                    </template>
                </component>

                <component
                    :is="DateRangeFilter"
                    v-else-if="filter.type === 'date-range'"
                    :class="filter.class"
                    :label="filter.label || trans('general.date_range')"
                    :max-date="filter.maxDate"
                    :min-date="filter.minDate"
                    :model-value="{ start: getValue(filter.startKey || 'start') || null, end: getValue(filter.endKey || 'end') || null }"
                    :teleport-popover="teleportPopovers"
                    @update:modelValue="onDateRangeUpdate(filter, $event)"
                />
            </template>
            <template v-if="$slots.addon" #addon>
                <slot name="addon" />
            </template>
        </collapsable-filter-bar>
    </div>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import CollapsableFilterBar from '@/Components/CollapsableFilterBar.vue';
import MultiSelectDropdown from '@/Components/MultiSelectDropdown.vue';
import SingleSelectDropdown from '@/Components/SingleSelectDropdown.vue';
import DateRangeFilter from '@/Components/DateRangeFilter.vue';
import SearchFilter from '@/Pages/WorkOrders/SearchFilter.vue';
import { trans } from '@/mixins/i18n.js';
import useQueryParams from '@/Utils/UseQueryParams.ts';

const props = defineProps({
    config: {
        type: Object,
        required: true,
    },
    modelValue: {
        // Parent should pass a ref(null); we'll initialize it
    },
    isOpen: {
        type: Boolean,
        default: null,
    },
    // Forwarded to the filter inputs so their popovers render in a body-level
    // portal. Set by consumers whose filter bar lives inside a containing
    // block (e.g. the dashboard header's `container-type`) where inline
    // popovers mis-anchor. See MultiSelectDropdown.teleportPopover.
    teleportPopovers: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'update:isOpen', 'change', 'clear']);
const processing = ref(false);

const isOpenState = computed({
    get() {
        return props.isOpen;
    },
    set(value) {
        emit('update:isOpen', value);
    },
});

const filters = computed(() => props.config?.filters || []);
const searchFilter = computed(() => filters.value.find((f) => f.type === 'search'));
const renderedFilters = computed(() =>
    filters.value.filter((f) => {
        if (f.type === 'search') return false;
        if (f.type === 'multi-select') {
            if (f.options === null) return true; // loading: include for skeleton
            const length = Array.isArray(f.options) ? f.options.length : 0;
            return length > 1;
        }
        if (f.type === 'single-select') {
            return Array.isArray(f.options) && f.options.length > 0;
        }
        return true;
    }),
);

// Initialize UseQueryParams inside the component using config defaults and route/options
const qp = useQueryParams({
    paramsWithDefaults: buildDefaultsFromConfig(),
    route: props.config?.route,
    options: props.config?.routeOptions || {},
    additionalData: props.config?.additionalData || (() => ({})),
    onLoadSuccess: props.config?.onLoadSuccess || (() => {}),
});

// Bind processing state
watch(
    () => qp.form.processing,
    (v) => (processing.value = !!v),
    { immediate: true },
);

// Initialize parent v-model with qp.form reference on mount
onMounted(() => {
    emit('update:modelValue', qp);
});

function buildDefaultsFromConfig() {
    const defaults = { page: 1 };
    (props.config?.filters || []).forEach((f) => {
        if (f.type === 'date-range') {
            const startKey = f.startKey || 'start';
            const endKey = f.endKey || 'end';
            defaults[startKey] = f.defaultStart ?? null;
            defaults[endKey] = f.defaultEnd ?? null;
        } else if (f.type === 'multi-select') {
            defaults[f.key] = Array.isArray(f.default) ? f.default : [];
        } else if (f.type === 'search') {
            defaults[f.key] = f.default ?? null;
        } else {
            defaults[f.key] = f.default ?? null;
        }
    });
    // Sorting default passthrough if provided in config
    if (props.config?.defaultSort) {
        defaults.sort = props.config.defaultSort;
    }

    Object.keys(props.config?.formData || {}).forEach((key) => {
        defaults[key] = props.config?.formData[key] ?? null;
    });

    return defaults;
}

function isEmptyValue(value) {
    if (Array.isArray(value)) {
        return value.length === 0;
    }
    return value === null || value === undefined || value === '';
}

const isActive = computed(() => {
    return filters.value.some((f) => {
        if (f.type === 'date-range') {
            const startKey = f.startKey || 'start';
            const endKey = f.endKey || 'end';
            return !isEmptyValue(getValue(startKey)) || !isEmptyValue(getValue(endKey));
        }
        return !isEmptyValue(getValue(f.key));
    });
});

function updateValue(key, value) {
    if (!qp?.form) return;
    qp.form[key] = value;
    qp.change();
    emit('change', key);
}

function onDateRangeUpdate(filter, value) {
    const startKey = filter.startKey || 'start';
    const endKey = filter.endKey || 'end';
    if (!qp?.form) return;
    qp.form[startKey] = value.start;
    qp.form[endKey] = value.end;
    qp.change();
    emit('change', 'date');
}

function clear() {
    if (!qp) return;
    qp.clear();
    emit('clear');
    emit('change', 'clear');
}

function onClear() {
    clear();
}

function getValue(key) {
    return qp?.form ? qp.form[key] : null;
}

defineExpose({ clear, updateValue });
</script>
